import i18next from 'i18next'
import React from 'react'
import { Link } from 'widgets-for-react'
import { PaginationLayout } from '../../../../pagination/paginationLayout'
import { getInCompanyFormUrl, daysCount, showAvailableSeats } from './courseDetailsBlockUtils'
import { EventAlternative, CoursePageData } from './courseDetailsBlockState'
import { displayUTCTime } from '../../../../utils/utils'
import { PriceListBlock } from './nestedBlocks/priceListBlock'
import { EventTemplate } from '../../../models/Events/Events'
import { CoursePage } from '../../cmsPageState'

export interface CourseDetailsProps {
  page?: number
  pageData: CoursePage
  eventTemplate: EventTemplate
  alternatives: EventAlternative[]
}

const GeneralInformationBlock = (props: CourseDetailsProps): JSX.Element => {
  const { pageData, eventTemplate, alternatives } = props
  const { events, products } = eventTemplate
  const eventDuration = events.length > 0 && `${daysCount(events)} ${daysCount(events) === 1 ? i18next.t('courses:day') : i18next.t('courses:days')}`
  const inCompanyEnabled = pageData.courseInCompany

  return <div className="block block--specs">
    <div className="block__inner container--s gutter--m spacing--m">
      <div className="block__content">
        <dl className="specs text--styled">
          {(eventDuration || pageData.courseDurationOverride) && <>
            <dt className="specs__term">
              {i18next.t('courses:duration')}:
            </dt>
            <dd className="specs__def">
              {pageData.courseDurationOverride || eventDuration}
            </dd>
          </>}

          {(alternatives.length > 0 || inCompanyEnabled ) && <>
            <dt className="specs__term">{i18next.t('courses:alternatives')}</dt>
            <dd className="specs__def">
              {inCompanyEnabled &&
                <div className="specs__variant">
                  <Link to="/incompany-en-maatwerktrainingen">
                    {i18next.t('courses:incompany')}
                  </Link>
                </div>
              }
              {alternatives.map(s => (
                <div className="specs__variant">
                  <Link to={s.url}>{s.title}</Link>
                </div>
              ))}
            </dd>
          </>}

          {inCompanyEnabled && <>
            <dt className="specs__term">{i18next.t('courses:incompany')}:</dt>
            <dd className="specs__def">
              <Link to={getInCompanyFormUrl()}>
                {i18next.t('courses:requestIncompanyOfferLinkLabel')}
              </Link>{' '}
              {i18next.t('courses:requestIncompanyOfferSuffix')}
            </dd>
          </>}

          <PriceListBlock products={products} />

        </dl>
      </div>
      {events && events.length > 0 &&
        <footer className="block__footer block__clickables">
          <div className="block__clickable">
            <Link
              to={`/opleidingen/inschrijven?eventId=${eventTemplate.id}`}
              className="button button--primary">
              <span className="button__label">
                {i18next.t('courses:defaultRegisterLabel')}
              </span>
            </Link>
          </div>
        </footer>
      }
    </div>
  </div>
}

const SessionTable = (pageData: CoursePage, eventData: EventTemplate, page?: number): JSX.Element => {
  const { events } = eventData
  // const eventSessions = List(Blocks).flatMap(block => block.Sessions).toArray()
  const totalEvents = events.length

  // Show fallback text when no courses are available.
  if (totalEvents === 0) return <div className="block__inner container--m gutter--m spacing--m">
    <header className="block__header block__header--intro">
      <h2 className="block__title title--m">
        {i18next.t('courses:datesAndLocations')}
      </h2>
      <div>{i18next.t('courses:noCourseDates')}</div>
    </header>
  </div>

  // Pagination options
  const amountOfCoursesPerPage = 15;
  page = page || 1
  const indexMax = page ? ((page * amountOfCoursesPerPage) - 1) : 0
  const indexMin = page ? ((page * amountOfCoursesPerPage - amountOfCoursesPerPage) - 1) : 0

  // Datatable information values
  var tableBlockTitle = pageData.courseStartDateAndLocationsIntroduction ?? i18next.t('courses:datesAndLocations')
  var tableBlockDescription = i18next.t('courses:defaultDatesAndLocationsIntro')
  var tableTitle = i18next.t('courses:allData')

  return <>
    <div className="block block--datatable">
      <div className="block__inner container--m gutter--m spacing--m">
        <header className="block__header block__header--intro">
          <h2 className="block__title title--m">{tableBlockTitle}</h2>
          <p>{tableBlockDescription}</p>
        </header>

        <div className="block__content">
          <>
            <h3 className="block__title title--s">{tableTitle}</h3>
            <table className="datatable">
              <thead>
                <tr>
                  <th className="narrow">{i18next.t('courses:startDate')}</th>
                  <th className="full">{i18next.t('courses:location')}</th>
                  <th className="narrow">{i18next.t('courses:startTime')}</th>
                  <th className="narrow">{i18next.t('courses:endTime')}</th>
                  {!pageData.courseHideAvailableSeats && <th className="medium"></th>}
                  {!pageData.courseHideSchedule && <th className="medium">{i18next.t('courses:schedule')}</th>}
                </tr>
              </thead>
              <tbody>
                {events.map((event, index) => {
                  const startDate = event.start_date && new Date(event.start_date)
                  const endDate = event.end_date && new Date(event.end_date)

                  const readableStartTime = startDate ? displayUTCTime(new Date(startDate)) : "n.v.t."
                  const readableEndTime = endDate ? displayUTCTime(new Date(endDate)) : "n.v.t."

                  return page && ((index > indexMin) && (index <= indexMax)) && (
                  <tr>
                    <td><time dateTime={startDate?.toUTCString()}>{startDate?.toLocaleDateString('nl-NL')}</time></td>
                    <td>{event.city}</td>
                    <td><time dateTime={startDate?.toUTCString()}>{readableStartTime}</time></td>
                    <td><time dateTime={endDate?.toUTCString()}>{readableEndTime}</time></td>
                    {!pageData.courseHideAvailableSeats && <td><strong className="datatable__extra text--s">{showAvailableSeats(event.availableSeats)}</strong></td>}
                    {!pageData.courseHideSchedule && <td className="text--styled">
                      <Link to={`/opleidingen/rooster?opl=${event.id}&ref=course`}>{i18next.t('courses:viewSchedule')}</Link>
                    </td>}
                  </tr>)
                })}
              </tbody>
            </table>
          </>
        </div>
      </div>
    </div>
    <PaginationLayout
      currentPage={page || 1}
      totalPages={Math.ceil(totalEvents / amountOfCoursesPerPage)}
      toPage={p => `${location.pathname}?${new URLSearchParams({ page: p?.toString() }).toString()}`}
    />
  </>
}

export const CourseDetailsBlockLayout = (props: CourseDetailsProps) => {
  const { pageData, eventTemplate: eventData, page } = props
  return (
    <div>
      {GeneralInformationBlock({...props})}
      {SessionTable(pageData, eventData, page)}
    </div>
  )
}
